/** Map action link shown below embedded maps (Google Maps, Apple Maps, Uber, etc.). */
export interface MapAction {
  type: string;
  label: string;
  url: string;
}

/** Map payload stored on a section or nested under composite section data/variant_config. */
export interface MapLocationData {
  address?: string;
  latitude?: number;
  longitude?: number;
  map_embed_url?: string;
  map_actions?: MapAction[];
  variant?: string;
  variant_config?: { layout?: string };
  steps_content?: Array<{ cta_button?: { link?: string } }>;
}

export const DEFAULT_MAP_COORDINATES = {
  address: 'DHL Tiel, Nederland',
  latitude: 51.8938831,
  longitude: 5.3859841,
} as const;

/** Decode HTML entities and ensure embed-friendly Google Maps iframe URL. */
export function normalizeMapEmbedUrl(url: string | null | undefined): string {
  if (!url) {
    return '';
  }

  let normalized = url.replace(/&amp;/g, '&').trim();

  // Regular maps links are not iframe-embeddable; rebuild from coordinates when possible.
  const coordMatch = normalized.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (
    coordMatch &&
    !normalized.includes('output=embed') &&
    (normalized.includes('google.com/maps') ||
      normalized.includes('maps.google.com'))
  ) {
    const [, lat, lng] = coordMatch;
    normalized = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  }

  return normalized;
}

export function buildMapEmbedUrl(lat: number, lng: number): string {
  return `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
}

export function buildMapActions(lat: number, lng: number): MapAction[] {
  return [
    {
      type: 'google_maps',
      label: 'Open in Google Maps',
      url: `https://maps.google.com/maps?q=${lat},${lng}`,
    },
    {
      type: 'apple_maps',
      label: 'Open in Apple Maps',
      url: `https://maps.apple.com/?q=${lat},${lng}`,
    },
    {
      type: 'uber',
      label: 'Book a ride',
      url: `https://m.uber.com/ul/?action=setPickup&dropoff[latitude]=${lat}&dropoff[longitude]=${lng}`,
    },
  ];
}

export const DEFAULT_MAP_DETAIL: MapLocationData = {
  map_actions: buildMapActions(
    DEFAULT_MAP_COORDINATES.latitude,
    DEFAULT_MAP_COORDINATES.longitude
  ),
  address: DEFAULT_MAP_COORDINATES.address,
  latitude: DEFAULT_MAP_COORDINATES.latitude,
  longitude: DEFAULT_MAP_COORDINATES.longitude,
  map_embed_url: buildMapEmbedUrl(
    DEFAULT_MAP_COORDINATES.latitude,
    DEFAULT_MAP_COORDINATES.longitude
  ),
  variant: 'full-width-map',
  variant_config: { layout: 'full-width-map' },
};

export function createDefaultMapDetail(): MapLocationData {
  return JSON.parse(JSON.stringify(DEFAULT_MAP_DETAIL)) as MapLocationData;
}

function extractDirectMapData(obj: unknown): MapLocationData | null {
  if (!obj || typeof obj !== 'object') {
    return null;
  }

  const item: any = obj;

  const hasCoords =
    item.latitude != null &&
    item.longitude != null &&
    !isNaN(Number(item.latitude)) &&
    !isNaN(Number(item.longitude));

  const hasEmbed =
    typeof item.map_embed_url === 'string' &&
    item.map_embed_url.trim().length > 0;

  const hasAddress =
    typeof item.address === 'string' && item.address.trim().length > 0;

  if (hasCoords || hasEmbed || hasAddress) {
    return item as MapLocationData;
  }

  return null;
}

/**
 * Extracts raw map location data from:
 * - A mapDetail object directly
 * - A section with variant_config.mapDetail (e.g. COMPONENT_RECRUITER root or data)
 * - A section with data.variant_config.mapDetail
 * - A section with data.location_map (e.g. COMPONENT_MEDIA, COMPONENT_APPLICATION_PROCESS)
 * - A standalone map section whose data contains address/coordinates/embed URL (e.g. COMPONENT_LOCATION_MAP)
 */
export function extractMapLocationData(
  source: unknown
): MapLocationData | null {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const obj: any = source;

  // 1. Nested in variant_config.mapDetail (e.g. COMPONENT_RECRUITER root or data)
  if (obj.variant_config?.mapDetail) {
    const res = extractDirectMapData(obj.variant_config.mapDetail);
    if (res) return res;
  }

  // 2. Nested in data.variant_config.mapDetail
  if (obj.data?.variant_config?.mapDetail) {
    const res = extractDirectMapData(obj.data.variant_config.mapDetail);
    if (res) return res;
  }

  // 3. Nested in mapDetail directly
  if (obj.mapDetail) {
    const res = extractDirectMapData(obj.mapDetail);
    if (res) return res;
  }

  // 4. Nested in data.mapDetail
  if (obj.data?.mapDetail) {
    const res = extractDirectMapData(obj.data.mapDetail);
    if (res) return res;
  }

  // 5. Nested in location_map (e.g. COMPONENT_MEDIA, COMPONENT_APPLICATION_PROCESS)
  if (obj.location_map) {
    const res = extractDirectMapData(obj.location_map);
    if (res) return res;
  }
  if (obj.data?.location_map) {
    const res = extractDirectMapData(obj.data.location_map);
    if (res) return res;
  }

  // 6. Direct map data on obj.data (e.g. COMPONENT_LOCATION_MAP section)
  if (obj.data) {
    const res = extractDirectMapData(obj.data);
    if (res) return res;
  }

  // 7. Direct map data on obj itself
  return extractDirectMapData(obj);
}

export function normalizeMapLocationData(
  data: MapLocationData | null | undefined
): MapLocationData | null {
  if (!data) {
    return null;
  }

  const lat =
    data.latitude != null && !isNaN(Number(data.latitude))
      ? Number(data.latitude)
      : undefined;
  const lng =
    data.longitude != null && !isNaN(Number(data.longitude))
      ? Number(data.longitude)
      : undefined;
  const hasCoords = lat != null && lng != null;

  const normalizedEmbed = normalizeMapEmbedUrl(data.map_embed_url);
  const map_embed_url =
    normalizedEmbed || (hasCoords ? buildMapEmbedUrl(lat!, lng!) : '');

  const map_actions =
    data.map_actions && data.map_actions.length > 0
      ? data.map_actions
      : hasCoords
        ? buildMapActions(lat!, lng!)
        : undefined;

  return {
    ...data,
    ...(lat != null ? { latitude: lat } : {}),
    ...(lng != null ? { longitude: lng } : {}),
    map_embed_url,
    ...(map_actions ? { map_actions } : {}),
  };
}

/** Resolve embedded map data from a composite or standalone section. */
export function resolveMapDetailFromSection(
  sectionData: Record<string, unknown> | null | undefined
): MapLocationData | null {
  if (!sectionData) {
    return null;
  }
  const extracted = extractMapLocationData(sectionData);
  return normalizeMapLocationData(extracted);
}

/**
 * Template object key for a location-map or map-bearing section.
 * Supports:
 * - Direct hint match (key or section.id or section.component_type)
 * - COMPONENT_LOCATION_MAP
 * - Legacy keys (job_location_map, location_map)
 * - Any section containing embedded map data (e.g. COMPONENT_RECRUITER with mapDetail)
 * - Single section passed as templateData
 */
export function findLocationMapTemplateKey(
  templateData: Record<string, unknown> | null | undefined,
  hint?: string
): string | null {
  if (!templateData) {
    return null;
  }

  const tpl: any = templateData;

  // Handle case where templateData is a single section object directly
  const isSectionObject =
    'component_type' in tpl ||
    'section_order' in tpl ||
    (typeof tpl['id'] === 'string' && 'data' in tpl);

  if (isSectionObject) {
    const id = tpl['id'] as string | undefined;
    if (hint && (id === hint || tpl['component_type'] === hint)) {
      return id || hint;
    }
    return id || hint || 'job_location_map';
  }

  // 1. Direct key match with hint
  if (hint && tpl[hint]) {
    return hint;
  }

  // 2. Section id or component_type match with hint
  if (hint) {
    for (const key of Object.keys(tpl)) {
      const section: any = tpl[key];
      if (section?.id === hint || section?.component_type === hint) {
        return key;
      }
    }
  }

  // 3. Standalone location map section (COMPONENT_LOCATION_MAP)
  for (const key of Object.keys(tpl)) {
    const section: any = tpl[key];
    if (section?.component_type === 'COMPONENT_LOCATION_MAP') {
      return key;
    }
  }

  // 4. Legacy fallback keys
  if (tpl['job_location_map']) {
    return 'job_location_map';
  }
  if (tpl['location_map']) {
    return 'location_map';
  }

  // 5. Any section in templateData with embedded map data (e.g. COMPONENT_RECRUITER with mapDetail)
  for (const key of Object.keys(tpl)) {
    const section: any = tpl[key];
    if (extractMapLocationData(section)) {
      return key;
    }
  }

  return null;
}

/**
 * Prefer nested map on composite sections; otherwise use root location-map section data.
 * Works seamlessly with:
 * - COMPONENT_RECRUITER (with variant_config.mapDetail)
 * - COMPONENT_LOCATION_MAP (with data.latitude, data.longitude, data.map_embed_url)
 * - COMPONENT_MEDIA / COMPONENT_APPLICATION_PROCESS (with location_map)
 * - Single section passed as templateData
 * - Full templateData dictionary with or without section hint
 */
export function resolveEmbeddedMapData(
  templateData: Record<string, unknown> | null | undefined,
  nested?: MapLocationData | Record<string, unknown> | null,
  hint?: string
): MapLocationData | null {
  // 1. Check nested map data if provided
  if (nested) {
    const extractedNested = extractMapLocationData(nested);
    if (
      extractedNested?.map_embed_url ||
      (extractedNested?.latitude != null &&
        extractedNested?.longitude != null) ||
      extractedNested?.address
    ) {
      return normalizeMapLocationData(extractedNested);
    }
  }

  if (!templateData) {
    return null;
  }

  const tpl: any = templateData;

  // 2. Check if templateData is a single section object directly
  const isSectionObject =
    'component_type' in tpl ||
    'section_order' in tpl ||
    (typeof tpl['id'] === 'string' && 'data' in tpl);

  if (isSectionObject) {
    const extracted = extractMapLocationData(tpl);
    if (extracted) {
      return normalizeMapLocationData(extracted);
    }
  }

  // 3. Resolve key from templateData using hint if provided
  const key = findLocationMapTemplateKey(templateData, hint);
  if (key && tpl[key]) {
    const section = tpl[key];
    const extracted = extractMapLocationData(section);
    if (extracted) {
      return normalizeMapLocationData(extracted);
    }
  }

  // 4. Direct extraction from templateData as fallback
  const extractedRoot = extractMapLocationData(tpl);
  if (extractedRoot) {
    return normalizeMapLocationData(extractedRoot);
  }

  return null;
}
